import { forwardRef, useState, type ButtonHTMLAttributes } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { Calendar, type CalendarRange } from "./Calendar";
import { Popover, PopoverTrigger, PopoverContent } from "./Popover";
import { Button } from "./Button";
import { Clock } from "@/icons";

const fmt = (d?: Date, locale?: string) =>
  d ? d.toLocaleDateString(locale, { month: "short", day: "numeric", year: "numeric" }) : "";

// forwardRef + prop spread so Radix's <PopoverTrigger asChild> can attach its
// onClick/ref/aria-* — without this the popover never opens.
const Trigger = forwardRef<
  HTMLButtonElement,
  { label: string; placeholder: string; invalid?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>
>(function Trigger({ label, placeholder, invalid, className, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      aria-invalid={invalid || undefined}
      className={cn(
        "inline-flex h-11 w-full items-center gap-2 rounded-xl border bg-input px-3.5 text-sm transition-colors",
        "focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring/45",
        "data-[state=open]:border-primary",
        invalid ? "border-danger" : "border-border",
        className,
      )}
      {...props}
    >
      <Clock className="h-4 w-4 shrink-0 text-fg-muted" />
      <span className={cn("truncate", label ? "text-fg" : "text-fg-subtle")}>{label || placeholder}</span>
    </button>
  );
});

export interface DatePickerProps {
  value?: Date | null;
  onChange: (d: Date) => void;
  placeholder?: string;
  invalid?: boolean;
  min?: Date;
  max?: Date;
}

export function DatePicker({ value, onChange, placeholder, invalid, min, max }: DatePickerProps) {
  const { t, i18n } = useTranslation("ui");
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Trigger
          label={value ? fmt(value, i18n.language) : ""}
          placeholder={placeholder ?? t("datePicker.pickDate")}
          invalid={invalid}
        />
      </PopoverTrigger>
      <PopoverContent align="start">
        <Calendar
          mode="single"
          selected={value}
          min={min}
          max={max}
          onSelect={(d) => {
            onChange(d);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

export interface DateRangePickerProps {
  value?: CalendarRange;
  onChange: (r: CalendarRange) => void;
  placeholder?: string;
  presets?: boolean;
}

export function DateRangePicker({ value, onChange, placeholder, presets = true }: DateRangePickerProps) {
  const { t, i18n } = useTranslation("ui");
  const [open, setOpen] = useState(false);
  const label =
    value?.from && value?.to
      ? `${fmt(value.from, i18n.language)} – ${fmt(value.to, i18n.language)}`
      : value?.from
        ? `${fmt(value.from, i18n.language)} – …`
        : "";

  const setPreset = (days: number) => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - days);
    onChange({ from, to });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Trigger label={label} placeholder={placeholder ?? t("datePicker.pickRange")} />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto">
        <div className="flex flex-col gap-3 sm:flex-row">
          {presets && (
            <div className="flex flex-row gap-1 sm:flex-col">
              {[
                [t("datePicker.presets.7d"), 7],
                [t("datePicker.presets.30d"), 30],
                [t("datePicker.presets.90d"), 90],
              ].map(([l, d]) => (
                <Button key={l as string} variant="ghost" size="sm" onClick={() => setPreset(d as number)}>
                  {l}
                </Button>
              ))}
            </div>
          )}
          <Calendar mode="range" range={value} onRangeChange={onChange} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
